import { SupabaseClient } from '@supabase/supabase-js'
import { Database } from '../types/database'
import { safeUrlForLog } from '../utils/safe-url'

export interface ErrorLogData {
  message: string
  errorStack?: string
  errorType?: string
  severity?: 'debug' | 'info' | 'warn' | 'error' | 'critical'
  userId?: string
  proId?: string
  userRole?: string
  userTimezone?: string
  url?: string
  method?: string
  statusCode?: number
  browserName?: string
  browserVersion?: string
  appName?: string
  environment?: string
  componentName?: string
  action?: string
  metadata?: Record<string, any>
}

/**
 * Deterministic grouping key. Same fingerprint = same bug (roughly).
 *
 * Strips variable + potentially-PII parts so:
 *   (a) 500 crashes of the same shape collapse into one row in the viewer
 *   (b) the fingerprint column never lands with an email/phone/SIN/UUID
 *       — even if a caller accidentally interpolated one into an error
 *       message. (The raw `message` column can still contain it; devs
 *       control that. This scrub only guards the dedup key.)
 */
export function computeFingerprint(
  errorType: string | undefined,
  message: string
): string {
  const type = (errorType || 'Error').trim()
  const normalized = message
    .replace(/[\w.+-]+@[\w.-]+\.[a-z]{2,}/gi, '<email>')
    .replace(/(?:\+?\d[\s\-()]*){7,}\d/g, '<phone>')
    .replace(/\b\d{3}[- ]?\d{3}[- ]?\d{3}\b/g, '<sin>')
    .replace(/\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi, '<uuid>')
    .replace(/["'`][^"'`\n]*["'`]/g, '"?"')
    .replace(/\b\d+\b/g, '<n>')
    .trim()
    .slice(0, 200)
  return `${type}::${normalized}`
}

function detectRelease(): string | undefined {
  return (
    process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA ||
    process.env.VERCEL_GIT_COMMIT_SHA ||
    process.env.NEXT_PUBLIC_APP_RELEASE ||
    undefined
  )
}

class ErrorTrackingService {
  constructor(private supabase: SupabaseClient<Database>) {
    // Validate the client has required methods
    if (!this.isValidClient()) {
      console.error('[ErrorTracking] Invalid Supabase client provided. Client may not be properly initialized.')
    }
  }
  
  private isValidClient(): boolean {
    return !!(this.supabase && typeof this.supabase.from === 'function')
  }

  /**
   * Log an error to the database
   * Sanitizes data to ensure HIPAA/SOC2 compliance
   */
  async logError(data: ErrorLogData): Promise<void> {
    try {
      // Check if client is valid before attempting to log
      if (!this.isValidClient()) {
        console.warn('[ErrorTracking] Cannot log error - Supabase client not properly initialized')
        console.log('[ErrorTracking] Error data that would have been logged:', data)
        return
      }
      
      // Sanitize metadata to remove any potential PII
      const sanitizedMetadata = this.sanitizeMetadata(data.metadata)
      
      // Get browser info if in browser environment
      const browserInfo = this.getBrowserInfo()
      
      // Generate a UUID for the id field
      const { v4: uuidv4 } = await import('uuid')
      
      const truncatedMessage = this.truncateString(data.message, 1000)
      const errorLog = {
        id: uuidv4(),
        message: truncatedMessage,
        errorStack: data.errorStack ? this.truncateString(data.errorStack, 5000) : null,
        errorType: data.errorType,
        severity: data.severity || 'error',
        userId: data.userId,
        proId: data.proId,
        userRole: data.userRole,
        userTimezone: data.userTimezone,
        url: data.url ?? safeUrlForLog(),
        method: data.method,
        statusCode: data.statusCode,
        browserName: data.browserName || browserInfo.name,
        browserVersion: data.browserVersion || browserInfo.version,
        appName: data.appName || this.detectAppName(),
        environment: process.env.NODE_ENV || 'production',
        componentName: data.componentName,
        action: data.action,
        metadata: sanitizedMetadata,
        fingerprint: computeFingerprint(data.errorType, truncatedMessage),
        release: detectRelease()
      }
      
      const { error } = await (this.supabase
        .from('error_logs') as any)
        .insert(errorLog)
      
      if (error) {
        // If we can't log to database, at least log to console
        console.error('[ErrorTracking] Failed to log error:', error)
        console.error('[ErrorTracking] Original error:', data)
      }
    } catch (err) {
      // Never let error logging throw and break the app
      console.error('[ErrorTracking] Critical failure:', err)
    }
  }
  
  /**
   * Get recent errors for a user
   */
  async getUserErrors(userId: string, limit = 50) {
    const { data, error } = await (this.supabase
      .from('error_logs') as any)
      .select('*')
      .eq('userId', userId)
      .order('createdAt', { ascending: false })
      .limit(limit)
    
    if (error) throw error
    return data
  }
  
  /**
   * Get recent errors for admin dashboard with pagination
   */
  async getRecentErrors(filters?: {
    severity?: string
    appName?: string
    errorType?: string
    limit?: number
    offset?: number
  }) {
    let query = (this.supabase
      .from('error_logs') as any)
      .select('*', { count: 'exact' })
      .order('createdAt', { ascending: false })
    
    if (filters?.severity) {
      query = query.eq('severity', filters.severity)
    }
    if (filters?.appName) {
      query = query.eq('appName', filters.appName)
    }
    if (filters?.errorType) {
      query = query.eq('errorType', filters.errorType)
    }
    
    // Apply pagination
    const limit = filters?.limit || 100
    const offset = filters?.offset || 0
    query = query.range(offset, offset + limit - 1)
    
    const { data, error, count } = await query
    
    if (error) throw error
    return { data, count }
  }
  
  /**
   * Get error statistics for dashboard
   */
  async getErrorStats(days = 7) {
    const startDate = new Date()
    startDate.setDate(startDate.getDate() - days)
    
    const { data, error } = await (this.supabase
      .from('error_logs') as any)
      .select('severity, errorType, appName, createdAt')
      .gte('createdAt', startDate.toISOString())
    
    if (error) throw error
    
    // Calculate stats
    const stats = {
      total: data?.length || 0,
      bySeverity: {} as Record<string, number>,
      byType: {} as Record<string, number>,
      byApp: {} as Record<string, number>,
      byDay: {} as Record<string, number>
    }
    
    data?.forEach((log: any) => {
      // By severity
      stats.bySeverity[log.severity] = (stats.bySeverity[log.severity] || 0) + 1
      
      // By type
      if (log.errorType) {
        stats.byType[log.errorType] = (stats.byType[log.errorType] || 0) + 1
      }
      
      // By app
      if (log.appName) {
        stats.byApp[log.appName] = (stats.byApp[log.appName] || 0) + 1
      }
      
      // By day
      const day = new Date(log.createdAt).toISOString().split('T')[0]!
      stats.byDay[day] = (stats.byDay[day] || 0) + 1
    })
    
    return stats
  }
  
  /**
   * Detect which app is running
   */
  private detectAppName(): string {
    if (typeof window === 'undefined') return 'server'
    
    const hostname = window.location.hostname
    const pathname = window.location.pathname
    
    if (hostname.includes('therapist') || pathname.startsWith('/therapist')) {
      return 'therapist'
    }
    if (pathname.startsWith('/admin')) {
      return 'admin'
    }
    return 'client'
  }
  
  /**
   * Get browser information
   */
  private getBrowserInfo(): { name: string | null; version: string | null } {
    if (typeof window === 'undefined') {
      return { name: null, version: null }
    }
    
    const ua = window.navigator.userAgent
    let name = null
    let version = null
    
    if (ua.includes('Chrome')) {
      name = 'Chrome'
      version = ua.match(/Chrome\/(\d+)/)?.[1] || null
    } else if (ua.includes('Safari')) {
      name = 'Safari'
      version = ua.match(/Version\/(\d+)/)?.[1] || null
    } else if (ua.includes('Firefox')) {
      name = 'Firefox'
      version = ua.match(/Firefox\/(\d+)/)?.[1] || null
    } else if (ua.includes('Edge')) {
      name = 'Edge'
      version = ua.match(/Edge\/(\d+)/)?.[1] || null
    }
    
    return { name, version }
  }
  
  /**
   * Sanitize metadata to ensure no PII is logged
   */
  private sanitizeMetadata(metadata?: Record<string, any>): Record<string, any> | null {
    if (!metadata) return null
    
    const sanitized: Record<string, any> = {}
    const sensitiveKeys = ['password', 'token', 'secret', 'ssn', 'sin', 'email', 'phone', 'address']
    
    for (const [key, value] of Object.entries(metadata)) {
      // Skip sensitive keys
      if (sensitiveKeys.some(sensitive => key.toLowerCase().includes(sensitive))) {
        continue
      }
      
      // Recursively sanitize nested objects
      if (typeof value === 'object' && value !== null) {
        sanitized[key] = this.sanitizeMetadata(value)
      } else {
        sanitized[key] = value
      }
    }
    
    return sanitized
  }
  
  /**
   * Truncate strings to prevent excessive storage
   */
  private truncateString(str: string, maxLength: number): string {
    if (str.length <= maxLength) return str
    return str.substring(0, maxLength - 3) + '...'
  }
}

export const createErrorTrackingService = (supabase: SupabaseClient<Database>) =>
  new ErrorTrackingService(supabase)